import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createManager } from 'utils/redux-injectors';
import { paginationSaga } from './saga';
import {
  GoToPageAction,
  LoadPageDataFailedAction,
  PaginationState,
  PaginationType,
  queryInfoWithPagiType,
  UpdatePageDataAction,
  UpdateUserStatusAction,
} from './types';

export const initialPaginationState: PaginationState[PaginationType] = {
  cur_page: 0,
  total_pages: 0,
  total_count: 0,
  page_size: 0,
  content: {},
  query_info: {
    filters: '',
    sorts: '',
    page: 0,
    page_size: 0,
    path: '',
  },
};

export const initialState: PaginationState = {
  pagiPortfolio: initialPaginationState,
  pagiSubscription: initialPaginationState,
  pagiUser: initialPaginationState,
  pagiCompany: initialPaginationState,
};

const slice = createSlice({
  name: 'pagination',
  initialState,
  reducers: {
    initPageData(state, action: PayloadAction<queryInfoWithPagiType>) {
      state[action.payload.pagiType].cur_page = action.payload.page;
      if (state[action.payload.pagiType].content[action.payload.page]) {
        state[action.payload.pagiType].content[action.payload.page].loading =
          true;
        state[action.payload.pagiType].content[action.payload.page].error =
          false;
      } else {
        state[action.payload.pagiType].content[action.payload.page] = {
          data: null,
          loading: true,
          error: false,
        };
      }
    },
    updatePageData(state, action: PayloadAction<UpdatePageDataAction>) {
      const { total_pages, total_count, current_page, page_size, contents } =
        action.payload;
      state[action.payload.pagiType].total_pages = total_pages;
      state[action.payload.pagiType].total_count = total_count;
      state[action.payload.pagiType].page_size = page_size;
      state[action.payload.pagiType].content[current_page] = {
        data: contents,
        loading: false,
        error: false,
      };
    },
    loadPageDataFailed(state, action: PayloadAction<LoadPageDataFailedAction>) {
      state[action.payload.pagiType].content[action.payload.page] = {
        data: null,
        loading: false,
        error: true,
      };
    },
    goToPage(state, action: PayloadAction<GoToPageAction>) {
      state[action.payload.pagiType].cur_page = action.payload.page;
    },
    resetPagination(state, action: PayloadAction<PaginationType>) {
      state[action.payload] = initialPaginationState;
    },
    clearData(state, action: PayloadAction<PaginationType>) {
      state[action.payload].cur_page = 0;
      state[action.payload].total_pages = 0;
      state[action.payload].total_count = 0;
      state[action.payload].page_size = 0;
      state[action.payload].content = {};
    },
    updateQueryInfo(state, action: PayloadAction<queryInfoWithPagiType>) {
      if (state[action.payload.pagiType]) {
        state[action.payload.pagiType].query_info = {
          ...action.payload,
          filters: action.payload.filters.trim(),
        };
      } else {
        state[action.payload.pagiType] = {
          ...initialPaginationState,
          query_info: {
            ...action.payload,
            filters: action.payload.filters.trim(),
          },
        };
      }
    },
    updateUserStatus(state, action: PayloadAction<UpdateUserStatusAction>) {
      const { pagiType, userId, status } = action.payload;
      const currentPage = state[pagiType].cur_page;
      const currentContent = state[pagiType].content[currentPage];

      if (currentContent?.data) {
        const updatedData = currentContent.data.map(user =>
          user.id === userId ? { ...user, status } : user,
        );
        state[pagiType].content[currentPage] = {
          ...currentContent,
          data: updatedData,
        };
      }
    },
  },
});

export const { actions: paginationActions, reducer: paginationReducers } =
  slice;

export const usePaginationSlice = () => ({ actions: slice.actions });

export const PaginationManager = createManager({
  name: `${slice.name}Manager`,
  key: slice.name,
  reducer: slice.reducer,
  saga: paginationSaga,
});

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = usePaginationSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
